import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trophy } from 'lucide-react';
import TimeIndicator from '../TimeIndicator';
import BracketTreeView from '../BracketTreeView';
import TournamentInfo from './TournamentInfo';
import { generateTournamentData } from '@/utils/tournamentDataGenerator';

interface Tournament {
  id: number;
  title: string;
  sport: string;
  startDate: string;
  endDate: string;
  location: string;
  tournamentFormat: string;
  matchType: 'singles' | 'doubles';
  partner?: string;
  status: 'upcoming' | 'active' | 'finished';
  nextMatch?: {
    opponent: string;
    court: string;
    time: string;
  };
  organizer?: string;
  time?: string;
  category?: string;
  gameType?: 'singles' | 'doubles';
  currentStage?: string;
}

interface TournamentSumGamesViewProps {
  tournament: Tournament;
}

const TournamentSumGamesView = ({ tournament }: TournamentSumGamesViewProps) => {
  const [showBrackets, setShowBrackets] = useState(false);
  
  const { matches, standings } = useMemo(() => {
    // Transform tournament data to match expected interface
    const tournamentEvent = {
      ...tournament,
      organizer: tournament.organizer || 'Organizador',
      time: tournament.time || '10:00',
      category: tournament.category || 'A',
      tournamentFormat: tournament.tournamentFormat as 'round-robin' | 'sumatoria-juegos' | 'eliminacion-directa'
    };
    return generateTournamentData(tournamentEvent);
  }, [tournament]);
  
  const isFinished = tournament.status === 'finished';
  
  // Generate different mock data based on tournament status
  const groupMatches = matches || (() => {
    const userTeam = tournament.partner ? `Tú/${tournament.partner}` : 'Tú';
    
    if (tournament.status === 'upcoming') {
      return [
        {
          id: 1,
          teams: userTeam,
          opponent: 'Por confirmar',
          date: 'TBD',
          time: 'TBD',
          court: 'TBD',
          result: null,
          status: 'on-time' as 'on-time' | 'delayed' | 'cancelled',
          isUpcoming: true,
          gamesFor: 0,
          gamesAgainst: 0
        },
        {
          id: 2,
          teams: userTeam,
          opponent: 'Por confirmar',
          date: 'TBD',
          time: 'TBD',
          court: 'TBD',
          result: null,
          status: 'on-time' as const,
          isUpcoming: true,
          gamesFor: 0,
          gamesAgainst: 0
        },
        {
          id: 3,
          teams: userTeam,
          opponent: 'Por confirmar',
          date: 'TBD',
          time: 'TBD',
          court: 'TBD',
          result: null,
          status: 'on-time' as const,
          isUpcoming: true,
          gamesFor: 0,
          gamesAgainst: 0
        }
      ];
    }
    
    if (tournament.status === 'active') {
      return [
        {
          id: 1,
          teams: userTeam,
          opponent: 'López/Martín',
          date: '15 Jul',
          time: '16:00',
          court: '2',
          result: 'Ganaste 5-3',
          status: 'on-time' as 'on-time' | 'delayed' | 'cancelled',
          isUpcoming: false,
          gamesFor: 5,
          gamesAgainst: 3
        },
        {
          id: 2,
          teams: userTeam,
          opponent: 'García/Rodríguez',
          date: '16 Jul',
          time: '10:30',
          court: '1',
          result: null,
          status: 'on-time' as const,
          isUpcoming: true,
          gamesFor: 0,
          gamesAgainst: 0
        },
        {
          id: 3,
          teams: userTeam,
          opponent: 'Silva/Torres',
          date: '17 Jul',
          time: '14:30',
          court: '3',
          result: null,
          status: 'on-time' as const,
          isUpcoming: true,
          gamesFor: 0,
          gamesAgainst: 0
        }
      ];
    }
    
    // finished status
    return [
      {
        id: 1,
        teams: userTeam,
        opponent: 'López/Martín',
        date: '15 Jul',
        time: '16:00',
        court: '2',
        result: 'Ganaste 5-3',
        status: 'on-time' as 'on-time' | 'delayed' | 'cancelled',
        isUpcoming: false,
        gamesFor: 5,
        gamesAgainst: 3
      },
      {
        id: 2,
        teams: userTeam,
        opponent: 'García/Rodríguez',
        date: '16 Jul',
        time: '10:30',
        court: '1',
        result: 'Perdiste 3-5',
        status: 'on-time' as const,
        isUpcoming: false,
        gamesFor: 3,
        gamesAgainst: 5
      },
      {
        id: 3,
        teams: userTeam,
        opponent: 'Silva/Torres',
        date: '16 Jul',
        time: '14:30',
        court: '3',
        result: 'Ganaste 6-2',
        status: 'on-time' as const,
        isUpcoming: false,
        gamesFor: 6,
        gamesAgainst: 2
      }
    ];
  })();

  const groupData = standings || (() => {
    const userTeam = tournament.partner ? `Tú/${tournament.partner}` : 'Tú';
    
    if (tournament.status === 'upcoming') {
      return [
        { position: 1, team: userTeam, wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, points: 0, isUser: true },
        { position: 1, team: 'García/Rodríguez', wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, points: 0, isUser: false },
        { position: 1, team: 'López/Martín', wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, points: 0, isUser: false },
        { position: 1, team: 'Silva/Torres', wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, points: 0, isUser: false }
      ];
    }
    
    if (tournament.status === 'active') {
      return [
        { position: 1, team: 'García/Rodríguez', wins: 1, losses: 0, gamesFor: 6, gamesAgainst: 2, points: 2, isUser: false },
        { position: 2, team: userTeam, wins: 1, losses: 0, gamesFor: 5, gamesAgainst: 3, points: 2, isUser: true },
        { position: 3, team: 'López/Martín', wins: 0, losses: 1, gamesFor: 3, gamesAgainst: 5, points: 0, isUser: false },
        { position: 4, team: 'Silva/Torres', wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, points: 0, isUser: false }
      ];
    }
    
    // finished status
    return [
      { position: 1, team: userTeam, wins: 2, losses: 1, gamesFor: 14, gamesAgainst: 10, points: 4, isUser: true },
      { position: 2, team: 'García/Rodríguez', wins: 2, losses: 1, gamesFor: 13, gamesAgainst: 11, points: 4, isUser: false },
      { position: 3, team: 'López/Martín', wins: 1, losses: 2, gamesFor: 10, gamesAgainst: 14, points: 2, isUser: false },
      { position: 4, team: 'Silva/Torres', wins: 1, losses: 2, gamesFor: 11, gamesAgainst: 13, points: 2, isUser: false }
    ];
  })();

  // Para Sumatoria de Juegos, siempre mostramos la vista de grupos, no importa el currentStage
  // Solo si el torneo específicamente dice ser eliminación directa lo tratamos como tal
  const isInEliminationPhase = false; // Para Sumatoria de Juegos, siempre mostramos grupos
  const isInGroupPhase = true; // Para Sumatoria de Juegos, siempre estamos en "fase de grupos"
  const totalMatchesInGroup = 3; // Total de partidos en el grupo
  
  console.log('TournamentSumGamesView - Tournament format is Sumatoria de Juegos');
  console.log('TournamentSumGamesView - currentStage:', tournament.currentStage);
  console.log('TournamentSumGamesView - Will show groups view with matches and standings');

  // Datos de ejemplo para los brackets programados
  const scheduledBrackets = {
    octavos: [
      { id: 1, player1: 'Ganador Grupo A', player2: 'Segundo Grupo B', date: '17 Jul', time: '09:00', court: '1' },
      { id: 2, player1: 'Ganador Grupo B', player2: 'Segundo Grupo A', date: '17 Jul', time: '09:30', court: '2' },
      { id: 3, player1: 'Ganador Grupo C', player2: 'Segundo Grupo D', date: '17 Jul', time: '10:00', court: '3' },
      { id: 4, player1: 'Ganador Grupo D', player2: 'Segundo Grupo C', date: '17 Jul', time: '10:30', court: '1' }
    ],
    cuartos: [
      { id: 5, player1: 'Ganador Octavo 1', player2: 'Ganador Octavo 2', date: '17 Jul', time: '15:00', court: '1' },
      { id: 6, player1: 'Ganador Octavo 3', player2: 'Ganador Octavo 4', date: '17 Jul', time: '15:30', court: '2' }
    ],
    semifinales: [
      { id: 7, player1: 'Ganador Cuarto 1', player2: 'Ganador Cuarto 2', date: '17 Jul', time: '18:00', court: '1' }
    ],
    final: [
      { id: 8, player1: 'Ganador Semifinal 1', player2: 'Ganador Semifinal 2', date: '17 Jul', time: '20:00', court: '1' }
    ]
  };

  // Función para obtener el badge de resultado
  const getResultBadge = (match: any) => {
    if (match.isUpcoming) {
      return (
        <Badge variant="outline" className="border-primary text-primary text-xs">
          Pendiente
        </Badge>
      );
    }

    if (match.gamesFor > match.gamesAgainst) {
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
          Ganaste
        </Badge>
      );
    } else if (match.gamesFor === match.gamesAgainst) {
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-800 text-xs">
          Empate
        </Badge>
      );
    } else {
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs">
          Perdiste
        </Badge>
      );
    }
  };

  if (isInEliminationPhase) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl font-bold text-gray-900">
              Bracket de Eliminación Directa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-primary/5 p-6 rounded-lg text-center">
              <p className="text-lg text-primary font-semibold">
                Fase de Eliminación Directa en Desarrollo
              </p>
              <p className="text-primary/80 mt-2">
                El bracket completo se mostrará aquí con tu camino resaltado.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showBrackets) {
    return (
      <BracketTreeView 
        onBack={() => setShowBrackets(false)}
        bracketData={scheduledBrackets}
        sport={tournament.sport}
        gameType={tournament.gameType || tournament.matchType}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">
          {tournament.status === 'upcoming' ? "Fase de Grupos - Próximo Torneo" : 
           tournament.status === 'finished' ? "Fase de Grupos - Finalizada" : 
           "Fase de Grupos - Grupo A"}
        </h3>
          
          {isFinished && groupData[0]?.isUser && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">🏆</span>
                </div>
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">
                  ¡Campeón del Torneo!
                </h4>
              </div>
              <p className="text-yellow-700 dark:text-yellow-300">
                {tournament.partner ? `Tú/${tournament.partner}` : 'Tú'} - ¡Felicidades por ganar el torneo!
              </p>
            </div>
          )}
      </div>
      
      {/* Partidos del Grupo - Formato vertical con scroll */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-bold text-gray-900">
            {tournament.status === 'upcoming' ? "Partidos Programados" : 
             tournament.status === 'finished' ? "Partidos Finalizados" : 
             "Partidos de mi Grupo"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-4 px-1 snap-x snap-mandatory scrollbar-hide">
              {groupMatches.map((match) => (
                <div 
                  key={match.id}
                  className={`min-w-[280px] w-[280px] h-48 border rounded-lg p-4 flex flex-col justify-between snap-start ${
                    match.isUpcoming ? 'border-primary/20 bg-primary/5' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex-1">
                    {/* Parejas y resultado */}
                    <div className="mb-3">
                      {match.result ? (
                        <div className="flex gap-3 items-start">
                          {/* Parejas lado izquierdo */}
                          <div className="flex-1 space-y-1">
                            <div className="text-xs font-medium text-primary bg-primary/5 px-2 py-1 rounded truncate">
                              {match.teams}
                            </div>
                            <div className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded truncate">
                              {match.opponent}
                            </div>
                          </div>
                          
                          {/* Scores lado derecho - Solo juegos finales */}
                          <div className="w-20 space-y-1">
                            <div className="text-center">
                              <div className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-bold">
                                {match.gamesFor}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="bg-gray-200 text-gray-600 px-2 py-1 rounded text-sm font-bold">
                                {match.gamesAgainst}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          {/* Parejas lado izquierdo */}
                          <div className="flex-1 space-y-1">
                            <div className="text-xs font-medium text-primary bg-primary/5 px-2 py-1 rounded truncate">
                              {match.teams}
                            </div>
                            <div className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded truncate">
                              {match.opponent}
                            </div>
                          </div>
                          
                          {/* VS lado derecho */}
                          <div className="w-20 flex items-center justify-center">
                            <div className="text-xs text-gray-400 font-medium">
                              VS
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Estado del partido con colores según resultado */}
                    <div className="mb-3">
                      {getResultBadge(match)}
                    </div>
                  </div>
                  
                  {/* Información de fecha, hora, cancha y tiempo */}
                  <div className="space-y-2 border-t pt-3">
                    <div className="flex justify-between items-center text-xs text-gray-600">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <span>📅 {match.date}</span>
                        </div>
                        <div className="flex items-center">
                          <span>🕐 {match.time}</span>
                        </div>
                        <div className="flex items-center">
                          <span>🎾 Cancha {match.court}</span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <TimeIndicator status={match.status} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Indicadores de scroll sutiles */}
            <div className="absolute top-0 right-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none" />
          </div>
        </CardContent>
      </Card>

      {/* Tabla de Posiciones del Grupo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="text-base font-semibold text-foreground">
              Mi Grupo: Grupo A
            </span>
            {!isFinished && (
              <Button 
                onClick={() => setShowBrackets(true)}
                className="text-[10px] px-1.5 py-0.5 h-5 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Trophy className="mr-0.5 h-2.5 w-2.5" />
                Ver Brackets
              </Button>
            )}
          </CardTitle>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
            <div className="w-3 h-3 bg-green-100 border border-green-200 rounded"></div>
            <span>Los primeros 2 lugares clasifican a la siguiente fase</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {groupData.map((team) => {
              const isQualified = team.position <= 2;
              const gameDifference = team.gamesFor - team.gamesAgainst;
              
              return (
                <div 
                  key={team.position}
                  className={`p-3 rounded-lg border transition-all duration-200 ${
                    isQualified 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-card border-border'
                  } ${
                    team.isUser 
                      ? 'ring-2 ring-primary/20 bg-primary/5' 
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    {/* Left section: Position, badges, and team */}
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      {/* Position */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        team.position === 1 ? 'bg-yellow-500 text-white' :
                        team.position === 2 ? 'bg-gray-400 text-white' :
                        team.position === 3 ? 'bg-orange-500 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {team.position}
                      </div>
                      
                      {/* Badges - solo TÚ, sin Clasifica */}
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        {team.isUser && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">
                            TÚ
                          </Badge>
                        )}
                      </div>
                      
                      {/* Team name - with proper overflow handling */}
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-sm truncate ${
                          team.isUser ? 'text-primary' : 'text-foreground'
                        }`}>
                          {team.team}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right section: Stats */}
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      {/* Record and Points */}
                      <div className="text-right">
                        <div className="text-xs font-medium text-foreground">
                          {team.wins}V-{team.losses}D
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {team.points} pts
                        </div>
                      </div>
                      
                      {/* Game difference */}
                      <div className="text-right min-w-[3rem]">
                        <div className={`text-sm font-semibold ${
                          gameDifference > 0 ? 'text-green-600' : 
                          gameDifference < 0 ? 'text-red-600' : 'text-muted-foreground'
                        }`}>
                          {gameDifference > 0 ? '+' : ''}{gameDifference}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {team.gamesFor}/{team.gamesAgainst}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TournamentSumGamesView;